import React,{useState,useEffect} from "react";
import { refreshNotifications } from "../utils/notifications";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { withRouter } from "../utils/router";
import { makeStyles } from 'tss-react/mui';
import { Menu, MenuItem, Button, IconButton, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import DoneIcon from "@mui/icons-material/DoneOutlined";
import VoicemailIcon from "@mui/icons-material/VoicemailOutlined";
import RecordingIcon from "@mui/icons-material/GraphicEqOutlined";
import SmsIcon from '@mui/icons-material/SmsOutlined';
import FaxIcon from "@mui/icons-material/FaxOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CallMissedIcon from "@mui/icons-material/CallMissedOutlined";
import {
  updateVoicemailViewed,
  updateCallJournal,
  updateRecordingsViewed,
  updateFaxes,
  updateConversationsViewed,
} from "../utils/utils";
import {
  setNotifications,
  setNotificationsLoading,
} from "../store/features/notification/notificationSlice";
import { setCalls } from "../store/features/calls/callSlice";
import { setVoicemails } from "../store/features/voicemails/voicemailSlice";
import { setSmsConversations } from "../store/features/smsConversations/smsConversationsSlice";
import { setFaxes } from "../store/features/faxes/faxSlice";
import { setRecordings } from "../store/features/recordings/recordingSlice";
import { resetSession } from "../store/features/session/sessionSlice";

const mapDispatchToProps = {
  setCalls,
  setVoicemails,
  setSmsConversations,
  setFaxes,
  setRecordings,
  setNotifications,
  setNotificationsLoading,
  resetSession,
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    width: 340,
    maxWidth: "calc(100vw - 32px)",
    marginTop: theme.spacing(0.5),
    borderRadius: 10,
    boxShadow: "0px 6px 24px rgba(0, 0, 0, 0.16)",
    "& ul": {
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2, 1.25),
    borderBottom: `1px solid ${theme.palette.divider}`,
    outline: "none",
  },
  title: {
    fontSize: 15,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  total: {
    fontSize: 12.5,
    color: theme.palette.text.secondary,
  },
  // One kind of notification: icon, name, how many, and a button to clear it.
  item: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minHeight: 52,
    padding: theme.spacing(0.75, 1, 0.75, 2),
    color: theme.palette.text.primary,
    textDecoration: "none",
  },
  itemIcon: {
    flexShrink: 0,
    width: 34,
    height: 34,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.main,
    backgroundColor: "rgba(1, 186, 239, 0.13)",
    "& svg": {
      fontSize: 18,
    },
  },
  itemIconDanger: {
    color: "#d0312d",
    backgroundColor: "rgba(229, 50, 45, 0.11)",
  },
  itemIconEmpty: {
    color: theme.palette.text.disabled,
    backgroundColor: theme.palette.action.hover,
  },
  itemText: {
    flex: 1,
    minWidth: 0,
    fontSize: 14.5,
    fontWeight: 500,
  },
  itemTextEmpty: {
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  count: {
    minWidth: 22,
    height: 22,
    padding: "0 7px",
    borderRadius: 11,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12.5,
    fontWeight: 600,
    color: "#fff",
    backgroundColor: theme.palette.primary.light,
  },
  clear: {
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.primary.light,
    },
  },
  // Keeps the rows aligned when a row has nothing to clear.
  clearSpace: {
    width: 34,
    flexShrink: 0,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: theme.spacing(0.75, 1),
    borderTop: `1px solid ${theme.palette.divider}`,
    "& button": {
      textTransform: "none",
      fontSize: 13.5,
      color: theme.palette.primary.light,
    },
  },
  caughtUp: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(4, 2),
    textAlign: "center",
    outline: "none",
    "& svg": {
      fontSize: 44,
      marginBottom: theme.spacing(0.5),
      color: "#2e9e55",
    },
  },
  caughtUpTitle: {
    fontSize: 15,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  caughtUpText: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
}));


const NotificationMenu = (props)=>{
    const {
      isOpen,
      setNotifications,
      onCloseFunction,
      menuAnchorElement,
      notifications,
      badgeSetter,
      session,
      resetSession,
      isLoading,
      settings,
      history,
    } = props;
    const { classes, cx } = useStyles();
    const [count,setCount] = useState(0);
    
    const getTotalNotifications = ()=>{
      let total =
        getCount("voicemail") +
        getCount("call") +
        getCount("fax") +
        getCount("recording") + 
        getCount("conversation")
      return total;
    }

    useEffect(() => {
      recount();
      // eslint-disable-next-line
    }, [notifications,isLoading]);

    const recount = () => {
      let total = getTotalNotifications();

      setCount(total);
      badgeSetter(total);
    }

    const getCount = (type)=>{
      if(settings.notifications[type]==="no") {
        return 0;
      }
      if (type === "conversation") {
        return notifications[type].reduce( function(total, record) {
          return total + record.unreadMessages;
        },0);
      }
      return notifications[type].length;
    };

    const notificationTypes = [
      {
        icon: <CallMissedIcon />,
        text: "Missed calls",
        type: "call",
        danger: true,
        count: getCount("call"),
      },
      {
        icon: <VoicemailIcon />,
        text: "New voicemails",
        type: "voicemail",
        count: getCount("voicemail"),
      },
      {
        icon: <FaxIcon />,
        text: "New faxes",
        type: "fax",
        count: getCount("fax"),
      },
      {
        icon: <RecordingIcon />,
        text: "New call recordings",
        type: "recording",
        count: getCount("recording"),
      },
      {
        icon: <SmsIcon />,
        text: "New SMS messages",
        type: "conversation",
        count: getCount("conversation"),
      },
    ];

    const refetchNotifications = () => refreshNotifications({
      values: { server: session.lastServer, token: session.token },
      setters: { setCalls, setFaxes, setRecordings, setVoicemails, setSmsConversations },
      setNotifications,
      setNotificationsLoading,
      // The menu changes the call journal: always the server's copy.
      freshJournal: true,
      freshSms: true,
      onUnauthorized: () => {
        resetSession();
        history.push('/login');
      },
    });

    const handleClearIndividual = (itemType) => {
      let values = {
        server: session.lastServer,
        token: session.token,
      };
      if(notifications[itemType].length>0){
        switch(itemType){
          case "call":
            values.call = notifications.call.map(item=>{
              return {
                jid: item.jid,
                viewed: "yes",
                type: item.type,
                direction: item.direction,
              };
            });

            try{
              updateCallJournal(values);
              refetchNotifications();
              onCloseFunction("notifications");
            } catch(error){
              console.error(error);
            }
            
            break;
          case "voicemail":
            values.voicemail = notifications.voicemail.map(item=>{
              return {
                vid: item.vid,
                viewed: "yes"
              }
            });

            try{
              updateVoicemailViewed(values);
              refetchNotifications();
              onCloseFunction("notifications");
            } catch(error){
              console.error(error);
            }
            
            break;
          case "recording":
            values.recording = notifications.recording.map((item) => {
              return {
                rid: item.rid,
                viewed: "yes",
                type: "new",
                title: item.title,
              };
            });
            try {
              updateRecordingsViewed(values);
              refetchNotifications();
              onCloseFunction("notifications");
            } catch (error) {
              console.error(error);
            }
            break;
          case "fax":
            values.fax = notifications.fax.map((item) => {
              return {
                fid: item.fid,
                viewed: "yes",
                type: "new",
                group: item.group,
                title: item.title,
              };
            });
            try {
              updateFaxes(values);
              refetchNotifications();
              onCloseFunction("notifications");
            } catch (error) {
              console.error(error);
            }
            break;
          case "conversation":
              values.conversation = notifications.conversation.map(item=>{
                return {
                  conversation_id: item.conversation_id,
                };
              });
  
              try{
                // Marks the conversations read, as Clear does on the Notifications page.
                updateConversationsViewed(values);
                refetchNotifications();
                onCloseFunction("notifications");
              } catch(error){
                console.error(error);
              }
              
              break;
          default:
            break;
        }
      }
    }

    const handleClearAll = () => {
      let values = {
        server: session.lastServer,
        token: session.token,
      };
      let callValues,vmValues,fValues,rValues;
      
      if (getTotalNotifications() > 0) {
        setNotificationsLoading(true);
        let vm,fax,rec,call;

        if(settings.notifications.call){
          callValues = { ...values };
          callValues.call = notifications.call.map((item) => {
              return {
                jid: item.jid,
                viewed: "yes",
                type: item.type,
                direction: item.direction,
              };
            });
          call = updateCallJournal(callValues);
        }

        if(settings.notifications.voicemail){
          vmValues = { ...values };
          vmValues.voicemail = notifications.voicemail.map((item) => {
            return {
              vid: item.vid,
              viewed: "yes",
            };
          });
          vm = updateVoicemailViewed(vmValues);
        }
        
        if(settings.notifications.fax){
          fValues = { ...values };
          fValues.fax = notifications.fax.map((item) => {
            return {
              fid: item.fid,
              title: item.title,
              viewed: "yes",
              group: item.group,
              type: "new",
            };
          });
          fax = updateFaxes(fValues);
        }

        if(settings.notifications.recording){
          rValues = { ...values };
          rValues.recording = notifications.recording.map((item) => {
            return {
              rid: item.rid,
              title: item.title,
              viewed: "yes",
              type: "new",
            };
          });
          rec = updateRecordingsViewed(rValues);
        }

        Promise.all([call,vm, fax, rec])
          .then((res) => {
            for (var i = 0; i < res.length; i++) {
              if (res[i] instanceof Error && res[i].message === "Unauthorized") {
                resetSession();
                history.push('/login');
                return;
              }
            }

            refetchNotifications();
            setNotificationsLoading(false);
          })
          .catch((err) => {
            setNotificationsLoading(false);
            console.error(err);
          });
        
      }
    };

    const renderNotifications = ()=>{
      // eslint-disable-next-line
        return notificationTypes.map((item) => {
          if(settings.notifications[item.type]==="yes"){
            const empty = !(item.count > 0);
            return (
              <MenuItem
                key={item.type}
                component={Link}
                to={`/dashboard/notifications/${item.type}`}
                onClick={() => onCloseFunction("notifications")}
                className={classes.item}
              >
                <span className={cx(classes.itemIcon, item.danger && classes.itemIconDanger, empty && classes.itemIconEmpty)}>
                  {item.icon}
                </span>
                <span className={cx(classes.itemText, empty && classes.itemTextEmpty)}>{item.text}</span>
                {empty ? <span className={classes.clearSpace} /> : (
                  <>
                    <span className={classes.count}>{item.count > 999 ? "999+" : item.count}</span>
                    <Tooltip title="Clear">
                      <IconButton
                        size="small"
                        aria-label={`clear ${item.text.toLowerCase()}`}
                        className={classes.clear}
                        onClick={(e) => {
                          // The row is a link; clearing must not follow it.
                          e.preventDefault();
                          e.stopPropagation();
                          handleClearIndividual(item.type);
                        }}
                      >
                        <DoneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </MenuItem>
            );
          }
      });
    };
    
    return (
      <Menu
        anchorEl={menuAnchorElement}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        id="notification-menu"
        keepMounted
        open={isOpen}
        onClose={(e) => onCloseFunction("notifications")}
        slotProps={{ paper: { className: classes.paper } }}
      >
        <li className={classes.header} tabIndex={-1}>
          <span className={classes.title}>Notifications</span>
          {count > 0 && <span className={classes.total}>{count} new</span>}
        </li>
        {count > 0 ? (
          renderNotifications()
        ) : (
          <li className={classes.caughtUp} tabIndex={-1}>
            <CheckCircleOutlinedIcon />
            <div className={classes.caughtUpTitle}>You're all caught up</div>
            <div className={classes.caughtUpText}>New calls, voicemails, faxes and messages show up here.</div>
          </li>
        )}

        {count > 0 ? (
          <li className={classes.footer}>
            <Button onClick={handleClearAll}>Clear all</Button>
          </li>
        ) : null}
      </Menu>
    );
};

NotificationMenu.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCloseFunction: PropTypes.func.isRequired,
  menuAnchorElement: PropTypes.object,
  badgeSetter: PropTypes.func.isRequired
};

const mapStateToProps = (state) => {
  return {
    settings: state.user.webapp,
    session: state.session,
    notifications: state.notifications,
    isLoading: state.notifications.loading,
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(NotificationMenu)
);